/**
 * §6.4 / §6.5 / §4.2 — build aggregation.
 *
 * Rebuilds `player.stats` from the owned passives (weapons contribute no player-wide
 * stats, §6.1), exposes the §6.4 exotic stats that have no `PlayerStats` field, and
 * publishes the §6.5 synergy / counter-synergy lookup that the level-up UI (§13.1.6)
 * and the weapon systems read.
 *
 * Determinism: pure function of the build. No RNG, no wall clock, no allocation after
 * module load (every table below is built once at import time).
 */

import { CAPTAIN_STAT_ADDS } from '../captain/traits';
import { cfg } from '../core/config';
import { PASSIVES, SYNERGIES, SHIP_FORM, weaponById, passiveById } from '../core/content';
import type { World } from './world';

/** §4.2 caps. ADD buckets are summed then clamped once. */
export const CAP_INCREASED_DAMAGE = 3.0;
export const CAP_HASTE = 1.0;
export const CAP_CRIT_CHANCE = 0.75;
export const CAP_CRIT_MULT = 4.0;
export const CAP_AREA = 1.5;
export const CAP_COUNT_ADD = 4;
export const CAP_PROJECTILE_SPEED = 1.0;
export const CAP_MOVE_SPEED = 0.4;
export const CAP_PICKUP_RADIUS = 1.0;
export const CAP_KNOCKBACK_MULT = 4.0;
/** §4.4 — armor + resist can never mitigate more than 80%. */
export const CAP_MITIGATION = 0.8;

/** §4.1 crit baseline: 5% chance, ×2.0 multiplier before bonuses. */
export const BASE_CRIT_CHANCE = 0.05;
export const BASE_CRIT_MULT = 2.0;

/** §9.5 — base Lumensurge tiers lost per hit, before passive.surge-capacitor. */
export const BASE_SURGE_DROP_TIERS = 2;
/** §6.4 — the drop can never fall below one tier (spine §2 tension preserved). */
export const MIN_SURGE_DROP_TIERS = 1;

/**
 * §6.4 stats that have no `PlayerStats` slot. One module-scope object, mutated in place
 * by `recomputeStats` — never reallocated, safe to read every tick.
 */
export interface BuildExtras {
  /** ADD bucket, 0..CAP_HASTE (§4.6). `attack_rate = base × (1 + haste)`. */
  haste: number;
  /** Seconds added to the dash cooldown (negative = faster); §03.4 floors it at 1.0 s. */
  dash_cooldown_add: number;
  /** Lumensurge tiers lost per hit (§9.5), already floored at MIN_SURGE_DROP_TIERS. */
  surge_drop_tiers: number;
  /** Multiplier on summon damage (§6.4 passive.symbiont-brood). */
  summon_damage_mult: number;
  /** Fractional damage-taken bonus applied to enemies inside the pickup radius. */
  aura_damage_amp: number;
  /** Flat cap of the overheal barrier (§6.4 passive.overflow-siphon); 50% conversion. */
  overheal_shield_cap: number;
  /** Fractional damage per kill-streak stack (10-stack cap, 4 s decay). */
  kill_streak_damage_per_stack: number;
  /** Multiplier applied to damage dealt inside the Focus-Aim cone (§6.4). */
  focus_aim_damage_mult: number;
  /**
   * Shards a spent bolt bursts into (passive.mitotic-bloom). 0 = the build has no split.
   * Rank r yields r + 1 shards: a "split" that produces one fragment is not a split, and
   * the +1 is what makes rank 1 read as a burst rather than a deflection.
   */
  split_shards: number;
  /**
   * Cascade depth in levels (passive.cascade-ganglion), 0..4. Level k strikes k enemies for
   * `2^-k` of the triggering bolt's snapshot, so the run reads 16 · 8 · 4 · 2 · 1 with the
   * bolt's own hit as the 16.
   */
  cascade_levels: number;
}

export const buildExtras: BuildExtras = {
  haste: 0,
  dash_cooldown_add: 0,
  surge_drop_tiers: BASE_SURGE_DROP_TIERS,
  summon_damage_mult: 1,
  aura_damage_amp: 0,
  overheal_shield_cap: 0,
  kill_streak_damage_per_stack: 0,
  focus_aim_damage_mult: 1,
  split_shards: 0,
  cascade_levels: 0,
};

/** §6.6 — hard ceilings on the two multiplying boons, mirrored by the passive `max_rank`. */
export const MAX_SPLIT_SHARDS = 4;
export const MAX_CASCADE_LEVELS = 4;

/**
 * AMD-39 — the pre-hold value of every stat `src/sim/hold.ts` rewrites per tick. `stepHold`
 * re-derives those four fields from this snapshot every tick rather than adding to the live
 * value, which is what keeps the write idempotent at zero payout. Kept per world so two
 * worlds in one process (tests, the probe harness) never share a base.
 */
export interface BuildBase {
  /** Clamped increased-damage ADD fraction (§4.2). */
  damage_add: number;
  /** Clamped haste ADD fraction (§4.6). */
  haste: number;
  /** Crit chance after the §4.2 cap. */
  crit_chance: number;
  /** Mitigation fraction after the §4.4 cap. */
  armor: number;
  /** Pickup-radius multiplier after the §4.2 ADD-bucket cap. */
  pickup_radius_mult: number;
}

const BASES = new WeakMap<World, BuildBase>();

export function buildBaseOf(w: World): BuildBase {
  let b = BASES.get(w);
  if (b === undefined) {
    b = { damage_add: 0, haste: 0, crit_chance: BASE_CRIT_CHANCE, armor: 0, pickup_radius_mult: 1 };
    BASES.set(w, b);
  }
  return b;
}

// --- content-derived tables, built once at module load ---

interface PassiveEffect {
  stat: string;
  magnitude_per_rank: number;
  unit: string;
}

interface PassiveRow {
  id: string;
  display_name: string;
  stat_effects: PassiveEffect[];
  max_rank: number;
  family: string;
}

interface SynergyRow {
  id: string;
  type: string;
  participants: string[];
  effect: string;
  magnitude: string;
}

const PASSIVE_ROWS = PASSIVES as unknown as PassiveRow[];
const SYNERGY_ROWS = SYNERGIES as unknown as SynergyRow[];

const passiveStat = new Map<string, string>();
const passiveMag = new Map<string, number>();
const passiveUnit = new Map<string, string>();
const passiveMaxRank = new Map<string, number>();

for (let i = 0; i < PASSIVE_ROWS.length; i++) {
  const row = PASSIVE_ROWS[i];
  const eff = row.stat_effects[0];
  passiveStat.set(row.id, eff.stat);
  passiveMag.set(row.id, eff.magnitude_per_rank);
  passiveUnit.set(row.id, eff.unit);
  passiveMaxRank.set(row.id, row.max_rank);
}

/** Content-order index of every synergy entry each ID participates in (§6.5). */
const synergyByParticipant = new Map<string, number[]>();
for (let i = 0; i < SYNERGY_ROWS.length; i++) {
  const parts = SYNERGY_ROWS[i].participants;
  for (let j = 0; j < parts.length; j++) {
    let list = synergyByParticipant.get(parts[j]);
    if (!list) {
      list = [];
      synergyByParticipant.set(parts[j], list);
    }
    list.push(i);
  }
}

/**
 * §6.5 counter efficiency. `"0% value (dead stat)"` → 0, `"... at 25% efficiency"` → 0.25.
 * Counters whose magnitude describes a behavioural loss (pierce alignment, trigger rate)
 * leave the stat scaling untouched at 1 — the loss is modelled by the owning weapon system.
 */
function parseEfficiency(magnitude: string): number {
  const m = /(\d+(?:\.\d+)?)%\s*(?:value|efficiency)/.exec(magnitude);
  if (m) return Number(m[1]) / 100;
  if (magnitude.indexOf('dead stat') >= 0) return 0;
  if (/^0\s/.test(magnitude)) return 0;
  return 1;
}

/** key = `${weapon_id}|${stat_key}` → efficiency multiplier in 0..1. */
const counterEfficiencyByWeaponStat = new Map<string, number>();
/** key = `${weapon_id}|${passive_id}` → true when the pairing is a §6.5 counter. */
const counterPairs = new Set<string>();
/** key = `${id_a}|${id_b}` (both orders) → index into SYNERGY_ROWS for positive synergies. */
const synergyPairs = new Map<string, number>();

for (let i = 0; i < SYNERGY_ROWS.length; i++) {
  const row = SYNERGY_ROWS[i];
  const a = row.participants[0];
  const b = row.participants[1];
  if (a === undefined || b === undefined) continue;
  if (row.type === 'counter') {
    counterPairs.add(a + '|' + b);
    counterPairs.add(b + '|' + a);
    const statA = passiveStat.get(a);
    const statB = passiveStat.get(b);
    const eff = parseEfficiency(row.magnitude);
    if (statB !== undefined) counterEfficiencyByWeaponStat.set(a + '|' + statB, eff);
    if (statA !== undefined) counterEfficiencyByWeaponStat.set(b + '|' + statA, eff);
  } else {
    if (!synergyPairs.has(a + '|' + b)) synergyPairs.set(a + '|' + b, i);
    if (!synergyPairs.has(b + '|' + a)) synergyPairs.set(b + '|' + a, i);
  }
}

/**
 * §6.5 — how much of a passive's stat actually reaches a weapon. 1 when no counter
 * applies, 0 for a declared dead stat. Weapon systems multiply their passive scaling
 * by this so the level-up UI's warning matches the sim exactly.
 */
export function counterEfficiency(weaponId: string, statKey: string): number {
  const v = counterEfficiencyByWeaponStat.get(weaponId + '|' + statKey);
  return v === undefined ? 1 : v;
}

/** §6.5 — true when the pairing is one of the 12 declared counter-synergies. */
export function isCounterPair(idA: string, idB: string): boolean {
  return counterPairs.has(idA + '|' + idB);
}

/** §6.5 — the positive synergy entry linking two IDs, or null. */
export function synergyEffectText(idA: string, idB: string): string {
  const i = synergyPairs.get(idA + '|' + idB);
  if (i === undefined) return '';
  return SYNERGY_ROWS[i].effect;
}

/** §6.5 — the counter entry linking two IDs, or ''. */
export function counterEffectText(idA: string, idB: string): string {
  for (let i = 0; i < SYNERGY_ROWS.length; i++) {
    const row = SYNERGY_ROWS[i];
    if (row.type !== 'counter') continue;
    const a = row.participants[0];
    const b = row.participants[1];
    if ((a === idA && b === idB) || (a === idB && b === idA)) return row.effect;
  }
  return '';
}

/** Stat key a passive drives, '' when unknown. */
export function passiveStatKey(passiveId: string): string {
  const v = passiveStat.get(passiveId);
  return v === undefined ? '' : v;
}

/** Per-rank magnitude of a passive, 0 when unknown. */
export function passiveMagnitudePerRank(passiveId: string): number {
  const v = passiveMag.get(passiveId);
  return v === undefined ? 0 : v;
}

/** Unit tag of a passive's stat (`percent` / `flat` / `flat_seconds` / `tiers` / `count`). */
export function passiveStatUnit(passiveId: string): string {
  const v = passiveUnit.get(passiveId);
  return v === undefined ? 'flat' : v;
}

export function passiveMaxRankOf(passiveId: string): number {
  const v = passiveMaxRank.get(passiveId);
  return v === undefined ? 5 : v;
}

// --- ownership queries (used by the offer pipeline and by counter flagging) ---

/** Rank of an owned passive, 0 when not owned. */
export function passiveRank(w: World, passiveId: string): number {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === passiveId) return p.passives[i].rank;
  }
  return 0;
}

/** Level of an owned weapon, 0 when not owned. Also matches the active slot. */
export function weaponLevel(w: World, weaponId: string): number {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === weaponId) return p.weapons[i].level;
  }
  return 0;
}

/** Slot index of an owned weapon, -1 when not owned. */
export function weaponSlotIndex(w: World, weaponId: string): number {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === weaponId) return i;
  }
  return -1;
}

/** True when the ID is a weapon, passive, active or evolution the player currently has. */
export function ownsId(w: World, id: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === id) return true;
    if (slot.evolved) {
      const def = weaponById.get(slot.def_id);
      if (def !== undefined && def.evolution_id === id) return true;
    }
  }
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) return true;
  }
  if (p.active !== null && p.active.def_id === id) return true;
  return false;
}

/**
 * §6.5 — does taking `itemId` form a counter pairing with anything already owned?
 * Drives the greyed "No effect on your current weapons" line in §13.1.6.
 */
export function counterPartnerFor(w: World, itemId: string): string {
  const idx = synergyByParticipant.get(itemId);
  if (idx === undefined) return '';
  for (let i = 0; i < idx.length; i++) {
    const row = SYNERGY_ROWS[idx[i]];
    if (row.type !== 'counter') continue;
    const other = row.participants[0] === itemId ? row.participants[1] : row.participants[0];
    if (ownsId(w, other)) return other;
  }
  return '';
}

/** §6.5 — the first positive synergy partner the player already owns, or ''. */
export function synergyPartnerFor(w: World, itemId: string): string {
  const idx = synergyByParticipant.get(itemId);
  if (idx === undefined) return '';
  for (let i = 0; i < idx.length; i++) {
    const row = SYNERGY_ROWS[idx[i]];
    if (row.type === 'counter') continue;
    const other = row.participants[0] === itemId ? row.participants[1] : row.participants[0];
    if (ownsId(w, other)) return other;
  }
  return '';
}

/** Display name for any weapon / passive / evolution ID; falls back to a titled slug. */
export function displayNameOf(id: string): string {
  const w = weaponById.get(id);
  if (w !== undefined) return w.display_name;
  const p = passiveById.get(id);
  if (p !== undefined) return p.display_name;
  return titleFromId(id);
}

/** `evolution.kingspear-medusa` → `Kingspear-Medusa` (§16 IDs carry no display name). */
export function titleFromId(id: string): string {
  const dot = id.indexOf('.');
  const slug = dot >= 0 ? id.slice(dot + 1) : id;
  const parts = slug.split('-');
  let out = '';
  for (let i = 0; i < parts.length; i++) {
    const s = parts[i];
    if (s.length === 0) continue;
    if (out.length > 0) out += '-';
    out += s.charAt(0).toUpperCase() + s.slice(1);
  }
  return out;
}

// --- AMD-50: the ship as a character sheet -------------------------------------------
//
// Three traits, one fixed pool of points, one derivation that produces BOTH the stat
// deltas and the silhouette. They cannot drift apart because they are the same twelve
// integers: `recomputeStats` reads them for the numbers and `src/render/ship.ts` reads
// the identical vector for the shape.
//
// Determinism: integer arithmetic over the owned build and nothing else. No RNG stream is
// read, no clock, no allocation after module load. Two players who paste the same share
// string and take the same picks fly the same ship with the same stats.

/** Index into a `pull` triple and into `ShipForm.points`. */
export const TRAIT_SPEED = 0;
export const TRAIT_DEFENCE = 1;
export const TRAIT_ATTACK = 2;
export const TRAIT_COUNT = 3;

/** Total points in the pool. Never changes: a build redistributes, it never earns more. */
export const TRAIT_TOTAL_POINTS = SHIP_FORM.point_buy.total_points;
/** The empty build's allocation — the §12 reference manta, exactly 4/4/4. */
export const TRAIT_BASELINE = SHIP_FORM.point_buy.baseline_per_trait;
/** Percentage points of one stat bought by one point above (or sold below) the baseline. */
export const TRAIT_PCT_PER_POINT = SHIP_FORM.point_buy.stat_pct_per_point;

const BASE_PULL = SHIP_FORM.point_buy.base_pull;
const WEAPON_UNIT = SHIP_FORM.point_buy.unit_pull_per_weapon_level;
const PASSIVE_UNIT = SHIP_FORM.point_buy.unit_pull_per_passive_rank;

/** Glyph vocabulary, in the order `src/render/ship.ts` draws them. */
export const GLYPHS: readonly string[] = ['spike', 'fork', 'blister', 'plate'];
/** Passive-mark stations, in the order `ShipForm.family` indexes them. */
export const FAMILY_STATIONS: readonly string[] = ['nose', 'crown', 'tail', 'keel'];

const weaponPull = new Map<string, readonly number[]>();
const weaponGlyph = new Map<string, number>();
for (let i = 0; i < SHIP_FORM.weapons.length; i++) {
  const row = SHIP_FORM.weapons[i];
  weaponPull.set(row.id, row.pull);
  weaponGlyph.set(row.id, Math.max(0, GLYPHS.indexOf(row.glyph)));
}

const statPull = new Map<string, readonly number[]>();
const statFamily = new Map<string, number>();
for (let i = 0; i < SHIP_FORM.passive_stats.length; i++) {
  const row = SHIP_FORM.passive_stats[i];
  statPull.set(row.stat, row.pull);
  statFamily.set(row.stat, Math.max(0, FAMILY_STATIONS.indexOf(row.station)));
}

/** Content sanity, asserted once at import: every shipped weapon and passive has a row. */
export function shipFormCoverage(): { weapons: string[]; stats: string[] } {
  const weapons: string[] = [];
  for (const [id] of weaponById) if (!weaponPull.has(id)) weapons.push(id);
  const stats: string[] = [];
  for (const [id] of passiveById) {
    const key = passiveStat.get(id);
    if (key !== undefined && !statPull.has(key)) stats.push(key);
  }
  return { weapons, stats };
}

/**
 * The whole ship, as integers. One per world, mutated in place, never reallocated.
 *
 * `signature` folds every field into one 32-bit value: the renderer compares it against
 * the hull it last built and rebuilds only when it moves, which is why a procedural hull
 * that responds to the build still costs one geometry upload per level-up rather than one
 * per frame.
 */
export interface ShipForm {
  /** Points in each trait. Always sums to {@link TRAIT_TOTAL_POINTS}. */
  readonly points: Int8Array;
  /** Raw pull totals behind `points`, kept for the UI's "why" line. */
  readonly pull: Int32Array;
  /** Per weapon slot: glyph index, or −1 for an empty slot. */
  readonly slotGlyph: Int8Array;
  /** Per weapon slot: 0 unevolved, 1 evolved. */
  readonly slotEvolved: Int8Array;
  /** Per passive station (nose / crown / tail / keel): summed ranks, 0 when none. */
  readonly familyRank: Int8Array;
  /** Concave bites earned by damage taken, 0..`wear.max_bites`. */
  bites: number;
  /** Depth level of the deepest bite, 1..`wear.bite_levels`; 0 when `bites` is 0. */
  biteLevel: number;
  /** Squared steps on the trailing edge earned by kills and bosses, 0..`max_kill_steps`. */
  killSteps: number;
  signature: number;
}

function createShipForm(): ShipForm {
  return {
    points: new Int8Array([TRAIT_BASELINE, TRAIT_BASELINE, TRAIT_BASELINE]),
    pull: new Int32Array(TRAIT_COUNT),
    slotGlyph: new Int8Array([-1, -1, -1, -1]),
    slotEvolved: new Int8Array(4),
    familyRank: new Int8Array(FAMILY_STATIONS.length),
    bites: 0,
    biteLevel: 0,
    killSteps: 0,
    signature: 0,
  };
}

const FORMS = new WeakMap<World, ShipForm>();

/**
 * The world's ship form. Refreshes the war-damage fields on every read — they are three
 * integer divisions over `RunCounters` and they must track a run that is still happening —
 * and leaves the build-derived fields alone; `recomputeStats` owns those.
 */
export function shipFormOf(w: World): ShipForm {
  let f = FORMS.get(w);
  if (f === undefined) {
    f = createShipForm();
    FORMS.set(w, f);
    recomputeShipTraits(w, f);
  }
  refreshWear(w, f);
  return f;
}

/**
 * §11 war damage, earned rather than rolled.
 *
 * Both quantities are step functions of `RunCounters`, which is snapshotted and restored
 * with the rest of the run, so a replay wears identically at the same tick and a resumed
 * run does not heal its scars. Quantised on purpose: a continuous scratch count would
 * rebuild the hull geometry on every point of chip damage, and the whole reason this is
 * integers is that the geometry is rebuilt only when one of them moves.
 */
function refreshWear(w: World, f: ShipForm): void {
  const cfgWear = SHIP_FORM.wear;
  let bites = Math.floor(w.counters.damage_taken / cfgWear.hp_per_bite);
  if (bites > cfgWear.max_bites) bites = cfgWear.max_bites;
  // Two stations, four steps: open the upper bow, open the lower, deepen the upper, deepen
  // the lower. `biteLevel` is the deepest of the two, kept for the signature and for a
  // dossier surface that has room to name it.
  const level = Math.ceil(bites / 2);
  let steps =
    Math.floor(w.counters.kills / cfgWear.kills_per_step) + w.counters.boss_kills * cfgWear.boss_kill_steps;
  if (steps > cfgWear.max_kill_steps) steps = cfgWear.max_kill_steps;
  f.bites = bites;
  f.biteLevel = level;
  f.killSteps = steps;
  f.signature = signatureOf(f);
}

/**
 * FNV-1a over the form's bytes. Written as a loop with an inlined mix rather than a helper
 * closure because the renderer calls this every frame through `shipFormOf`, and a closure
 * allocated per frame is exactly what §14.3 forbids.
 */
function signatureOf(f: ShipForm): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < TRAIT_COUNT; i++) {
    h = Math.imul((h ^ (f.points[i] & 0xff)) >>> 0, 16777619) >>> 0;
  }
  for (let i = 0; i < 4; i++) {
    h = Math.imul((h ^ ((f.slotGlyph[i] + 1) & 0xff)) >>> 0, 16777619) >>> 0;
    h = Math.imul((h ^ (f.slotEvolved[i] & 0xff)) >>> 0, 16777619) >>> 0;
  }
  for (let i = 0; i < f.familyRank.length; i++) {
    h = Math.imul((h ^ (familyLevelOf(f.familyRank[i]) & 0xff)) >>> 0, 16777619) >>> 0;
  }
  h = Math.imul((h ^ (f.bites & 0xff)) >>> 0, 16777619) >>> 0;
  h = Math.imul((h ^ (f.biteLevel & 0xff)) >>> 0, 16777619) >>> 0;
  h = Math.imul((h ^ (f.killSteps & 0xff)) >>> 0, 16777619) >>> 0;
  return h >>> 0;
}

/**
 * Summed rank → drawn level. The renderer keys its geometry rebuild off the signature, so
 * the signature must move when the DRAWING moves and not when a stat does: banding the rank
 * here is what turns "rebuild on every pick" into "rebuild at most three times per station
 * per run". Mirrors `familyLevel` in `src/render/ship.ts`, and a test asserts they agree.
 */
export function familyLevelOf(rank: number): number {
  if (rank <= 0) return 0;
  if (rank < 5) return 1;
  if (rank < 10) return 2;
  return 3;
}

/**
 * Re-derive the point-buy from the owned build.
 *
 * Re-derived, never incremented: the vector is a function of what you own right now, so
 * pick order cannot change it, a duplicate grant cannot double it, and a snapshot restore
 * reproduces it without carrying any extra state.
 *
 * Largest remainder over integers. `raw[t] · TOTAL` and `q[t] · S` are exact in f64 for
 * every reachable magnitude (max raw is 6 + 4·5·3 + 25·5·2 = 316, so the products are
 * well under 2^53), which is what makes the allocation identical on every machine.
 */
function recomputeShipTraits(w: World, f: ShipForm): void {
  const p = w.player;
  const raw = f.pull;
  for (let t = 0; t < TRAIT_COUNT; t++) raw[t] = BASE_PULL;
  for (let i = 0; i < 4; i++) {
    f.slotGlyph[i] = -1;
    f.slotEvolved[i] = 0;
  }
  for (let i = 0; i < f.familyRank.length; i++) f.familyRank[i] = 0;

  for (let i = 0; i < p.weapon_count && i < 4; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '') continue;
    const glyph = weaponGlyph.get(slot.def_id);
    if (glyph !== undefined) {
      f.slotGlyph[i] = glyph;
      f.slotEvolved[i] = slot.evolved ? 1 : 0;
    }
    const pull = weaponPull.get(slot.def_id);
    if (pull === undefined) continue;
    const level = slot.level > 0 ? slot.level : 1;
    for (let t = 0; t < TRAIT_COUNT; t++) raw[t] += pull[t] * level * WEAPON_UNIT;
  }

  for (let i = 0; i < p.passive_count; i++) {
    const slot = p.passives[i];
    if (slot.rank <= 0 || slot.def_id === '') continue;
    const key = passiveStat.get(slot.def_id);
    if (key === undefined) continue;
    const fam = statFamily.get(key);
    if (fam !== undefined) {
      const sum = f.familyRank[fam] + slot.rank;
      f.familyRank[fam] = sum > 127 ? 127 : sum;
    }
    const pull = statPull.get(key);
    if (pull === undefined) continue;
    for (let t = 0; t < TRAIT_COUNT; t++) raw[t] += pull[t] * slot.rank * PASSIVE_UNIT;
  }

  let sum = 0;
  for (let t = 0; t < TRAIT_COUNT; t++) sum += raw[t];
  let assigned = 0;
  let bestRem = -1;
  let bestT = 0;
  let secondRem = -1;
  let secondT = 1;
  for (let t = 0; t < TRAIT_COUNT; t++) {
    const num = raw[t] * TRAIT_TOTAL_POINTS;
    const q = Math.floor(num / sum);
    f.points[t] = q;
    assigned += q;
    const rem = num - q * sum;
    // Strict `>` keeps the tie-break on trait order (speed, then defence, then attack),
    // which is what makes an exact three-way tie land on 4/4/4 rather than anywhere.
    if (rem > bestRem) {
      secondRem = bestRem;
      secondT = bestT;
      bestRem = rem;
      bestT = t;
    } else if (rem > secondRem) {
      secondRem = rem;
      secondT = t;
    }
  }
  const left = TRAIT_TOTAL_POINTS - assigned;
  if (left > 0) f.points[bestT]++;
  if (left > 1) f.points[secondT]++;
  if (left > 2) f.points[3 - bestT - secondT]++;
}

/** Signed distance of each trait from the 4/4/4 baseline. Always sums to zero. */
export function traitDelta(f: ShipForm, trait: number): number {
  return f.points[trait] - TRAIT_BASELINE;
}

// --- stat aggregation ---

function clampAdd(sum: number, cap: number): number {
  return sum > cap ? cap : sum;
}

/**
 * §4.2 — rebuild `player.stats` and `buildExtras` from the owned passives.
 * Called on every build change (pick applied, evolution granted, run reset).
 * Allocation-free: every accumulator is a local number.
 */
export function recomputeStats(w: World): void {
  const p = w.player;
  const s = p.stats;

  let damagePct = 0;
  let hastePct = 0;
  let movePct = 0;
  let hpFlat = 0;
  let armorFlat = 0;
  let xpPct = 0;
  let pickupPct = 0;
  let critChancePct = 0;
  let critDmgPct = 0;
  let areaPct = 0;
  let durationPct = 0;
  let projSpeedPct = 0;
  let knockPct = 0;
  let regen = 0;
  let dashCd = 0;
  let surgeDrop = 0;
  let countAdd = 0;
  let summonPct = 0;
  let auraPct = 0;
  let overheal = 0;
  let streakPct = 0;
  let focusPct = 0;
  let splitAdd = 0;
  let cascadeAdd = 0;

  for (let i = 0; i < p.passive_count; i++) {
    const slot = p.passives[i];
    if (slot.rank <= 0 || slot.def_id === '') continue;
    const key = passiveStat.get(slot.def_id);
    if (key === undefined) continue;
    const mag = (passiveMag.get(slot.def_id) as number) * slot.rank;
    switch (key) {
      case 'damage_pct': damagePct += mag; break;
      case 'attack_speed_pct': hastePct += mag; break;
      case 'move_speed_pct': movePct += mag; break;
      case 'max_hp_flat': hpFlat += mag; break;
      case 'armor_flat': armorFlat += mag; break;
      case 'xp_gain_pct': xpPct += mag; break;
      case 'pickup_radius_pct': pickupPct += mag; break;
      case 'crit_chance_pct': critChancePct += mag; break;
      case 'crit_damage_pct': critDmgPct += mag; break;
      case 'area_pct': areaPct += mag; break;
      case 'duration_pct': durationPct += mag; break;
      case 'projectile_speed_pct': projSpeedPct += mag; break;
      case 'knockback_pct': knockPct += mag; break;
      case 'regen_hp_per_s': regen += mag; break;
      case 'dash_cooldown_s': dashCd += mag; break;
      case 'surge_drop_tiers': surgeDrop += mag; break;
      case 'projectile_count_add': countAdd += mag; break;
      case 'summon_damage_pct': summonPct += mag; break;
      case 'aura_damage_amp_pct': auraPct += mag; break;
      case 'overheal_shield_cap_flat': overheal += mag; break;
      case 'kill_streak_damage_pct': streakPct += mag; break;
      case 'focus_aim_damage_pct': focusPct += mag; break;
      case 'split_shards_add': splitAdd += mag; break;
      case 'cascade_levels_add': cascadeAdd += mag; break;
      default: break;
    }
  }

  // AMD-50 — the point-buy, spent. Derived from the same owned build the loop above read,
  // then folded into the ADD buckets BEFORE the §4.2 caps, so a build already at the
  // increased-damage ceiling buys nothing more with attack points and the caps still bind.
  //
  // Zero-sum by construction: the three points always sum to twelve, so `dSpd + dDef +
  // dAtk = 0` and every point moved into one stat is a point taken out of another. Each
  // point is worth `TRAIT_PCT_PER_POINT` percent of exactly one leg of the power triangle
  // — move speed, effective HP, damage — which is what makes "no spend dominates" an
  // arithmetic statement rather than an opinion: the product
  // `(1+.02a)(1+.02d)(1+.02s)` under `a+d+s=0` is maximised at the 4/4/4 baseline.
  // The captain's boon and flaw, landing in the same ADD buckets and under the same §4.2
  // caps. Two of the nine are non-zero on any given captain; the rest add zero. The pair is
  // priced against one rank of the cheapest passive that survived enumeration, so a captain
  // is always a smaller move than a level-up the player actually chose — worst reachable
  // pair −1.15%, best +1.67%, mean flat. Published from the run seed, so a headless harness
  // that never builds a captain reads the untouched baseline.
  damagePct += CAPTAIN_STAT_ADDS.damage_pct;
  movePct += CAPTAIN_STAT_ADDS.move_speed_pct;
  hpFlat += CAPTAIN_STAT_ADDS.max_hp_flat;
  armorFlat += CAPTAIN_STAT_ADDS.armor_flat;
  xpPct += CAPTAIN_STAT_ADDS.xp_gain_pct;
  pickupPct += CAPTAIN_STAT_ADDS.pickup_radius_pct;
  critChancePct += CAPTAIN_STAT_ADDS.crit_chance_pct;
  regen += CAPTAIN_STAT_ADDS.regen_hp_per_s;
  dashCd += CAPTAIN_STAT_ADDS.dash_cooldown_s;

  let form = FORMS.get(w);
  if (form === undefined) {
    form = createShipForm();
    FORMS.set(w, form);
  }
  recomputeShipTraits(w, form);
  refreshWear(w, form);
  movePct += TRAIT_PCT_PER_POINT * traitDelta(form, TRAIT_SPEED);
  damagePct += TRAIT_PCT_PER_POINT * traitDelta(form, TRAIT_ATTACK);
  hpFlat += (cfg.get('player.max_hp') * TRAIT_PCT_PER_POINT * traitDelta(form, TRAIT_DEFENCE)) / 100;

  const haste = clampAdd(hastePct / 100, CAP_HASTE);

  // AMD-39 — snapshot the four fields `stepHold` rewrites, before any hold bonus lands.
  const base = buildBaseOf(w);
  base.damage_add = clampAdd(damagePct / 100, CAP_INCREASED_DAMAGE);
  base.haste = haste;
  base.crit_chance = clampAdd(BASE_CRIT_CHANCE + critChancePct / 100, CAP_CRIT_CHANCE);
  base.armor = Math.min(armorFlat / 100, CAP_MITIGATION);
  base.pickup_radius_mult = 1 + clampAdd(pickupPct / 100, CAP_PICKUP_RADIUS);

  s.damage_mult = 1 + clampAdd(damagePct / 100, CAP_INCREASED_DAMAGE);
  s.area_mult = 1 + clampAdd(areaPct / 100, CAP_AREA);
  s.speed_mult = 1 + clampAdd(projSpeedPct / 100, CAP_PROJECTILE_SPEED);
  s.duration_mult = 1 + durationPct / 100;
  s.cooldown_mult = 1 / (1 + haste);
  s.count_add = clampAdd(countAdd, CAP_COUNT_ADD);
  s.crit_chance = clampAdd(BASE_CRIT_CHANCE + critChancePct / 100, CAP_CRIT_CHANCE);
  s.crit_mult = clampAdd(BASE_CRIT_MULT + critDmgPct / 100, CAP_CRIT_MULT);
  s.move_speed_mult = 1 + clampAdd(movePct / 100, CAP_MOVE_SPEED);
  s.pickup_radius_mult = 1 + clampAdd(pickupPct / 100, CAP_PICKUP_RADIUS);
  s.max_hp_add = hpFlat;
  // §4.4 — armor is a mitigation fraction; passives.json states it in percentage points
  // (`armor_flat` +2/rank → +10% at rank 5), clamped by the §4.2 total-mitigation cap.
  s.armor = Math.min(armorFlat / 100, CAP_MITIGATION);
  s.regen_per_s = regen;
  s.xp_gain_mult = 1 + xpPct / 100;
  s.luck = 0;
  s.knockback_mult = clampAdd(1 + knockPct / 100, CAP_KNOCKBACK_MULT);

  buildExtras.haste = haste;
  buildExtras.dash_cooldown_add = dashCd;
  const drop = BASE_SURGE_DROP_TIERS + surgeDrop;
  buildExtras.surge_drop_tiers = drop < MIN_SURGE_DROP_TIERS ? MIN_SURGE_DROP_TIERS : drop;
  buildExtras.summon_damage_mult = 1 + summonPct / 100;
  buildExtras.aura_damage_amp = auraPct / 100;
  buildExtras.overheal_shield_cap = overheal;
  buildExtras.kill_streak_damage_per_stack = streakPct / 100;
  buildExtras.focus_aim_damage_mult = 1 + focusPct / 100;
  // §6.6 — both are integer counts, not ADD-bucket fractions, and both are clamped here so a
  // future duplicate grant or a rank table edit cannot widen the projectile budget by accident.
  buildExtras.split_shards = splitAdd > 0 ? Math.min(Math.round(splitAdd) + 1, MAX_SPLIT_SHARDS) : 0;
  buildExtras.cascade_levels = cascadeAdd > 0 ? Math.min(Math.round(cascadeAdd), MAX_CASCADE_LEVELS) : 0;

  // Max HP is a build stat: growth is granted as usable HP, shrink clamps (§4.2).
  const baseMax = cfg.get('player.max_hp');
  const newMax = baseMax + hpFlat;
  if (newMax > p.max_hp) p.hp += newMax - p.max_hp;
  p.max_hp = newMax;
  if (p.hp > p.max_hp) p.hp = p.max_hp;
}

/** §10.9 — effective pickup radius in arena units (base 3.2 u × the §4.2 stat). */
export function pickupRadius(w: World): number {
  return cfg.get('player.pickup_radius') * w.player.stats.pickup_radius_mult;
}
